import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@shared/components/ui/form';
import { keycloakService } from '@features/auth/services/keycloak.service';
import { forgotPasswordSchema, type ForgotPasswordFormValues } from './auth-schemas';

interface ForgotPasswordFormProps {
  onShowLogin: () => void;
}

export function ForgotPasswordForm({ onShowLogin }: ForgotPasswordFormProps) {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    try {
      await keycloakService.forgotPassword(values.email);
      setSubmittedEmail(values.email);
      toast.success('Password reset link sent');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to send reset link');
    }
  };

  if (submittedEmail) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
          <MailCheck className="size-6 text-primary" />
        </div>
        <h2 className="mb-2 text-2xl font-semibold text-foreground">Check your email</h2>
        <p className="mb-8 text-pretty text-sm text-muted-foreground">
          If an account exists for{' '}
          <span className="font-medium text-foreground">{submittedEmail}</span>, we&apos;ve sent a
          link to reset your password.
        </p>
        <Button type="button" onClick={onShowLogin} className="h-11 w-full font-medium">
          Back to sign in
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="mb-8 text-center">
        <h2 className="mb-2 text-2xl font-semibold text-foreground">Reset your password</h2>
        <p className="text-pretty text-sm text-muted-foreground">
          Enter your email and we&apos;ll send you a link to reset your password.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    className="h-11"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="h-11 w-full font-medium" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send reset link'}
          </Button>
        </form>
      </Form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onShowLogin}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
        >
          <ArrowLeft className="size-4" />
          Back to sign in
        </button>
      </div>
    </>
  );
}
